import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

const CONSENT_KEY = "pa_cookie_consent_v1";

export default function CookieBanner() {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefsAllowed, setPrefsAllowed] = useState(true);

  useEffect(() => {
    try {
      if (!localStorage.getItem(CONSENT_KEY)) setVisible(true);
    } catch { /* ignore */ }
  }, []);

  const save = (consent) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, necessary: true, at: new Date().toISOString() }));
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  const pt = language !== "en";

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[70] bg-[#071A35] border-t border-[#D4A85D]/30 shadow-[0_-8px_30px_rgba(0,0,0,0.4)]"
      data-testid="cookie-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
          <p className="text-white/80 text-xs sm:text-sm leading-relaxed flex-1">
            {pt
              ? "Utilizamos cookies estritamente necessários ao funcionamento do site e cookies de preferências (idioma). Saiba mais na nossa "
              : "We use cookies strictly necessary for the website to work and preference cookies (language). Learn more in our "}
            <Link to={pt ? "/cookies" : "/cookie-policy"} className="text-[#D4A85D] underline hover:text-[#c9994a]" data-testid="cookie-policy-link">
              {pt ? "Política de Cookies" : "Cookie Policy"}
            </Link>
            {pt ? " e " : " and "}
            <Link to={pt ? "/privacidade" : "/privacy"} className="text-[#D4A85D] underline hover:text-[#c9994a]">
              {pt ? "Política de Privacidade" : "Privacy Policy"}
            </Link>
            .
          </p>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowPrefs((v) => !v)}
              className="border border-white/30 hover:border-[#D4A85D] text-white/80 hover:text-[#D4A85D] font-semibold rounded-md px-4 py-2.5 text-xs uppercase tracking-wider"
              data-testid="cookie-prefs-btn"
            >
              {pt ? "Preferências" : "Preferences"}
            </button>
            <button
              onClick={() => save({ accepted: false, preferences: false })}
              className="border border-white/30 hover:border-white text-white/80 hover:text-white font-semibold rounded-md px-4 py-2.5 text-xs uppercase tracking-wider"
              data-testid="cookie-refuse-btn"
            >
              {pt ? "Recusar" : "Refuse"}
            </button>
            <button
              onClick={() => save({ accepted: true, preferences: true })}
              className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-6 py-2.5 text-xs uppercase tracking-wider"
              data-testid="cookie-accept-btn"
            >
              {pt ? "Aceitar" : "Accept"}
            </button>
          </div>
        </div>

        {showPrefs && (
          <div className="mt-4 border-t border-white/10 pt-4 space-y-3" data-testid="cookie-prefs-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-white text-xs sm:text-sm font-semibold">{pt ? "Estritamente necessários" : "Strictly necessary"}</p>
                <p className="text-white/55 text-xs">{pt ? "Sessão, autenticação e segurança. Sempre ativos." : "Session, authentication and security. Always on."}</p>
              </div>
              <input type="checkbox" checked disabled className="accent-[#D4A85D] w-4 h-4 opacity-60" data-testid="cookie-necessary-toggle" />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-white text-xs sm:text-sm font-semibold">{pt ? "Preferências" : "Preferences"}</p>
                <p className="text-white/55 text-xs">{pt ? "Memorização do idioma escolhido (PT/EN)." : "Remembering your chosen language (PT/EN)."}</p>
              </div>
              <input
                type="checkbox"
                checked={prefsAllowed}
                onChange={(e) => setPrefsAllowed(e.target.checked)}
                className="accent-[#D4A85D] w-4 h-4 cursor-pointer"
                data-testid="cookie-preferences-toggle"
              />
            </div>
            <button
              onClick={() => save({ accepted: true, preferences: prefsAllowed })}
              className="bg-white/10 hover:bg-white/20 border border-[#D4A85D]/50 text-[#D4A85D] font-bold rounded-md px-5 py-2 text-xs uppercase tracking-wider"
              data-testid="cookie-save-prefs-btn"
            >
              {pt ? "Guardar preferências" : "Save preferences"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
